import { type AssistantAction, isRecord, list, num, str } from '../api';

/**
 * Round-3 ris shapes (ris docs/viewer-ai-tools-contract.md, "Asistente Guru — radiologist tools"),
 * normalised like api.ts does for messages: the cards read only what was checked here. Lengths are
 * cut to the contract's bounds again, so a server (or a model behind it) that sends more never
 * stretches a card or a report.
 */

// --- tool switches (§1) ------------------------------------------------------------------------------

export const TOOL_KEYS = [
  'send_to_report', 'report_check', 'report_compose', 'patient_context', 'prepare_study',
  'followups', 'recist', 'key_images', 'patient_summary',
] as const;
export type ToolKey = (typeof TOOL_KEYS)[number];

export interface ToolSwitches {
  /** The org's AI switch: off, no tool shows. */
  enabled: boolean;
  tools: Record<ToolKey, boolean>;
}

/** Every tool off: what the viewer assumes until (and unless) ris answers `GET /ai/tools`. */
export function noTools(): ToolSwitches {
  return { enabled: false, tools: Object.fromEntries(TOOL_KEYS.map((k) => [k, false])) as Record<ToolKey, boolean> };
}

/** Only a literal `true` switches a tool on; unknown keys are dropped, missing ones are off. */
export function normaliseTools(value: unknown): ToolSwitches {
  const out = noTools();
  if (!isRecord(value)) return out;
  out.enabled = value.enabled === true;
  const tools = isRecord(value.tools) ? value.tools : {};
  for (const key of TOOL_KEYS) out.tools[key] = tools[key] === true;
  return out;
}

// --- report sections (§2.1) ------------------------------------------------------------------------

export const REPORT_SECTIONS = ['technique', 'comparison', 'findings', 'impression', 'recommendations'] as const;
export type ReportSection = (typeof REPORT_SECTIONS)[number];

export const SECTION_LABELS: Record<ReportSection, string> = {
  technique: 'Técnica',
  comparison: 'Comparación',
  findings: 'Hallazgos',
  impression: 'Impresión',
  recommendations: 'Recomendaciones',
};

/** Names a model or an older route may use for a section. */
const SECTION_ALIASES: Record<string, ReportSection> = { recommendation: 'recommendations', tecnica: 'technique' };

export function reportSection(value: unknown): ReportSection | null {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  if ((REPORT_SECTIONS as readonly string[]).includes(key)) return key as ReportSection;
  return SECTION_ALIASES[key] ?? null;
}

/** ris accepts 1–8000 characters per insert. */
export const MAX_INSERT_TEXT = 8000;

export interface InsertResult {
  reportId: string | null;
  section: ReportSection | null;
  rowVersion: number | null;
}

export function normaliseInsert(value: unknown): InsertResult {
  const raw = isRecord(value) ? value : {};
  return { reportId: str(raw.report_id), section: reportSection(raw.section), rowVersion: num(raw.row_version) };
}

function cut(value: unknown, max: number): string | null {
  const text = str(value)?.trim();
  return text ? text.slice(0, max) : null;
}

/** The trace every model-backed answer carries. */
export interface ModelTrace {
  model: string | null;
  modelVersion: string | null;
  isTest: boolean;
}

function trace(raw: Record<string, unknown>): ModelTrace {
  return { model: str(raw.model), modelVersion: str(raw.model_version), isTest: raw.is_test === true };
}

// --- 2.2 report check ---------------------------------------------------------------------------------

export const ISSUE_KINDS = ['laterality', 'missing_finding', 'units', 'contradiction', 'critical_uncommunicated', 'other'] as const;
export type IssueKind = (typeof ISSUE_KINDS)[number];
export const ISSUE_SEVERITIES = ['critical', 'warning', 'info'] as const;
export type IssueSeverity = (typeof ISSUE_SEVERITIES)[number];

export interface ReportIssue {
  kind: IssueKind;
  severity: IssueSeverity;
  section: ReportSection | null;
  excerpt: string | null;
  message: string;
}

export interface ReportCheck extends ModelTrace {
  issues: ReportIssue[];
  reportId: string | null;
}

const MAX_EXCERPT = 200;
const MAX_ISSUE_MESSAGE = 400;

function oneOf<T extends string>(allowed: readonly T[], value: unknown, fallback: T): T {
  return (allowed as readonly unknown[]).includes(value) ? (value as T) : fallback;
}

export function normaliseIssue(value: unknown): ReportIssue | null {
  if (!isRecord(value)) return null;
  const message = cut(value.message, MAX_ISSUE_MESSAGE);
  if (!message) return null;
  return {
    kind: oneOf(ISSUE_KINDS, value.kind, 'other'),
    severity: oneOf(ISSUE_SEVERITIES, value.severity, 'info'),
    section: reportSection(value.section),
    excerpt: cut(value.excerpt, MAX_EXCERPT),
    message,
  };
}

/** Critical first, then warnings, then notes; the server's order within each. */
export function sortIssues(issues: ReportIssue[]): ReportIssue[] {
  const rank = (i: ReportIssue) => ISSUE_SEVERITIES.indexOf(i.severity);
  return issues.map((issue, at) => ({ issue, at })).sort((a, b) => rank(a.issue) - rank(b.issue) || a.at - b.at).map((x) => x.issue);
}

export function normaliseReportCheck(value: unknown): ReportCheck {
  const raw = isRecord(value) ? value : {};
  return { ...trace(raw), issues: sortIssues(list(raw.issues, normaliseIssue)), reportId: str(raw.report_id) };
}

// --- 2.3 compose -----------------------------------------------------------------------------------------

export interface ComposedSection {
  section: ReportSection;
  text: string;
}

export interface Composed extends ModelTrace {
  /** In report order, known sections only, empty ones dropped. */
  sections: ComposedSection[];
  template: { id: string; name: string } | null;
}

function normaliseTemplate(value: unknown): { id: string; name: string } | null {
  if (!isRecord(value)) return null;
  const id = value.id === undefined || value.id === null ? null : String(value.id);
  const name = str(value.name);
  return id && name ? { id, name } : null;
}

export function normaliseComposed(value: unknown): Composed {
  const raw = isRecord(value) ? value : {};
  const given = isRecord(raw.sections) ? raw.sections : {};
  const byKey = new Map<ReportSection, string>();
  for (const [key, text] of Object.entries(given)) {
    const section = reportSection(key);
    const body = cut(text, MAX_INSERT_TEXT);
    if (section && body && !byKey.has(section)) byKey.set(section, body);
  }
  const sections = REPORT_SECTIONS.filter((s) => byKey.has(s)).map((section) => ({ section, text: byKey.get(section) as string }));
  return { ...trace(raw), sections, template: normaliseTemplate(raw.template) };
}

// --- 2.4 patient context ---------------------------------------------------------------------------------

export interface Reception {
  diagnosis: string | null;
  referringDoctor: string | null;
  note: string | null;
  priority: string | null;
  critical: boolean;
}

export interface Procedure {
  code: string | null;
  name: string;
  modality: string | null;
}

export interface PriorReport {
  accession: string | null;
  signedAt: string | null;
  impression: string | null;
  procedures: string[];
}

export const FOLLOWUP_STATUSES = ['pending', 'done', 'cancelled'] as const;
export type FollowupStatus = (typeof FOLLOWUP_STATUSES)[number];

export interface Followup {
  id: string;
  dueOn: string | null;
  modality: string | null;
  reason: string;
  status: FollowupStatus;
  guideline: string | null;
}

export interface PatientContext {
  reception: Reception | null;
  procedures: Procedure[];
  priors: PriorReport[];
  followups: Followup[];
}

const MAX_CONTEXT_TEXT = 600;
const MAX_PRIORS = 10;

function normaliseReception(value: unknown): Reception | null {
  if (!isRecord(value)) return null;
  return {
    diagnosis: cut(value.diagnosis, MAX_CONTEXT_TEXT),
    referringDoctor: cut(value.referring_doctor, MAX_CONTEXT_TEXT),
    note: cut(value.note, MAX_CONTEXT_TEXT),
    priority: cut(value.priority, 40),
    critical: value.critical === true,
  };
}

function normaliseProcedure(value: unknown): Procedure | null {
  if (!isRecord(value)) return null;
  const name = cut(value.name, 200);
  return name ? { code: cut(value.code, 40), name, modality: cut(value.modality, 16) } : null;
}

function normalisePrior(value: unknown): PriorReport | null {
  if (!isRecord(value)) return null;
  const procedures = Array.isArray(value.procedures) ? value.procedures.map((p) => cut(p, 200)).filter((p): p is string => p !== null) : [];
  const prior = { accession: cut(value.accession, 64), signedAt: str(value.signed_at), impression: cut(value.impression_excerpt, MAX_CONTEXT_TEXT), procedures };
  return prior.accession || prior.impression || procedures.length > 0 ? prior : null;
}

export function normaliseFollowup(value: unknown): Followup | null {
  if (!isRecord(value)) return null;
  const id = value.id === undefined || value.id === null ? null : String(value.id);
  const reason = cut(value.reason, 500);
  if (!id || !reason) return null;
  return {
    id,
    dueOn: str(value.due_on),
    modality: cut(value.modality, 16),
    reason,
    status: oneOf(FOLLOWUP_STATUSES, value.status, 'pending'),
    guideline: cut(value.guideline, 120),
  };
}

export function normaliseContext(value: unknown): PatientContext {
  const raw = isRecord(value) ? value : {};
  return {
    reception: normaliseReception(raw.reception),
    procedures: list(raw.procedures, normaliseProcedure),
    priors: list(raw.priors, normalisePrior).slice(0, MAX_PRIORS),
    followups: list(raw.followups, normaliseFollowup),
  };
}

export interface ModelText extends ModelTrace {
  text: string;
}

export function normaliseSummary(value: unknown): ModelText {
  const raw = isRecord(value) ? value : {};
  return { ...trace(raw), text: cut(raw.summary, MAX_CONTEXT_TEXT) ?? '' };
}

// --- 2.5 prepare -----------------------------------------------------------------------------------------

export interface PrepareAnswer extends ModelTrace {
  reply: string;
  actions: AssistantAction[];
}

/** The action types "Preparar estudio" may run (contract `allowed_actions`), at most MAX_PREPARE_ACTIONS. */
export const PREPARE_ACTIONS: ReadonlySet<string> = new Set(['open_series', 'set_window', 'open_prior', 'sync_prior', 'set_layout']);
export const MAX_PREPARE_ACTIONS = 6;

function normaliseAction(value: unknown): AssistantAction | null {
  if (!isRecord(value)) return null;
  const type = str(value.type);
  if (!type || !PREPARE_ACTIONS.has(type)) return null;
  return { type, label: str(value.label) ?? '', args: isRecord(value.args) ? value.args : {} };
}

export function normalisePrepare(value: unknown): PrepareAnswer {
  const raw = isRecord(value) ? value : {};
  return { ...trace(raw), reply: cut(raw.reply, 400) ?? '', actions: list(raw.actions, normaliseAction).slice(0, MAX_PREPARE_ACTIONS) };
}

// --- 2.6 follow-up ---------------------------------------------------------------------------------------

export interface FollowupSuggestion extends ModelTrace {
  guideline: string | null;
  /** 0 = the guideline asks for no follow-up. */
  intervalMonths: number;
  modality: string | null;
  text: string | null;
  citation: string | null;
}

const MAX_INTERVAL = 60;

export function normaliseSuggestion(value: unknown): FollowupSuggestion | null {
  const raw = isRecord(value) ? value : {};
  const s = isRecord(raw.suggestion) ? raw.suggestion : null;
  if (!s) return null;
  const months = num(s.interval_months);
  const out: FollowupSuggestion = {
    ...trace(raw),
    guideline: cut(s.guideline, 120),
    intervalMonths: months === null ? 0 : Math.min(MAX_INTERVAL, Math.max(0, Math.round(months))),
    modality: cut(s.modality, 16),
    text: cut(s.text, MAX_CONTEXT_TEXT),
    citation: cut(s.citation, 300),
  };
  return out.text || out.guideline ? out : null;
}

// --- 2.7 RECIST ------------------------------------------------------------------------------------------

export const RECIST_RESPONSES = ['CR', 'PR', 'SD', 'PD', 'NE'] as const;
export type RecistResponse = (typeof RECIST_RESPONSES)[number];

export interface RecistTimepoint {
  studyUid: string;
  date: string | null;
  sumMm: number | null;
  lesions: Record<string, number | null>;
  complete: boolean;
}

export interface RecistTable {
  targets: Array<{ id: string; label: string }>;
  timepoints: RecistTimepoint[];
  baseline: number | null;
  nadir: number | null;
  current: number | null;
  response: RecistResponse;
  changeFromBaselinePct: number | null;
  changeFromNadirPct: number | null;
  text: string | null;
}

const MAX_TARGETS = 5;

function normaliseTarget(value: unknown): { id: string; label: string } | null {
  if (!isRecord(value) || value.id === undefined || value.id === null) return null;
  const label = cut(value.label, 120);
  return label ? { id: String(value.id), label } : null;
}

function positive(value: unknown): number | null {
  const n = num(value);
  return n !== null && n >= 0 ? n : null;
}

function normaliseTimepoint(value: unknown): RecistTimepoint | null {
  if (!isRecord(value)) return null;
  const studyUid = str(value.study_uid);
  if (!studyUid) return null;
  const date = str(value.date);
  const lesions: Record<string, number | null> = {};
  if (isRecord(value.lesions)) for (const [id, mm] of Object.entries(value.lesions)) lesions[id] = positive(mm);
  return { studyUid, date: date && /^\d{8}$/.test(date) ? date : null, sumMm: positive(value.sum_mm), lesions, complete: value.complete === true };
}

function index(value: unknown, length: number): number | null {
  const n = num(value);
  return n !== null && Number.isInteger(n) && n >= 0 && n < length ? n : null;
}

export function normaliseRecist(value: unknown): RecistTable {
  const raw = isRecord(value) ? value : {};
  const timepoints = list(raw.timepoints, normaliseTimepoint);
  return {
    targets: list(raw.targets, normaliseTarget).slice(0, MAX_TARGETS),
    timepoints,
    baseline: index(raw.baseline, timepoints.length),
    nadir: index(raw.nadir, timepoints.length),
    current: index(raw.current, timepoints.length),
    response: oneOf(RECIST_RESPONSES, raw.response, 'NE'),
    changeFromBaselinePct: num(raw.change_from_baseline_pct),
    changeFromNadirPct: num(raw.change_from_nadir_pct),
    text: cut(raw.text, MAX_INSERT_TEXT),
  };
}

// --- 2.9 patient summary ---------------------------------------------------------------------------------

export const MAX_PATIENT_SUMMARY = 4000;

export interface ApprovedSummary {
  text: string;
  approvedAt: string | null;
}

export interface PatientSummaryDraft {
  reportId: string | null;
  text: string;
  model: string | null;
  isTest: boolean;
  approved: ApprovedSummary | null;
}

export function normaliseApproved(value: unknown): ApprovedSummary | null {
  if (!isRecord(value)) return null;
  const text = cut(value.text, MAX_PATIENT_SUMMARY);
  return text ? { text, approvedAt: str(value.approved_at) } : null;
}

export function normalisePatientSummary(value: unknown): PatientSummaryDraft {
  const raw = isRecord(value) ? value : {};
  return {
    reportId: str(raw.report_id),
    text: cut(raw.text, MAX_PATIENT_SUMMARY) ?? '',
    model: str(raw.model),
    isTest: raw.is_test === true,
    approved: normaliseApproved(raw.approved),
  };
}
